/**
 * @module engine3d/scene
 * @summary Compiles the live PFD document into a flat, render-ready 3D scene.
 * @description
 *   The renderer wants one thing: a big array of world-space triangles it can
 *   sort and fill. This module produces exactly that from `S.shapes`,
 *   `S.connections` and `S.levels` — resolving each symbol to its 3D model,
 *   standing it on its floor, sweeping every connector into pipework, and
 *   adding the steelwork that carries the upper levels.
 *
 *   Rebuilding is O(triangles) and happens only when the DOCUMENT changes —
 *   never when the camera moves. On a 60-symbol drawing that is a few
 *   milliseconds, so a rebuild on every edit is imperceptible.
 *
 *   Per-triangle edge data (`Mesh.feat`, kit spec §4) is carried through
 *   verbatim into `Scene.feat`, so the renderer can draw the feature lines and
 *   silhouettes the primitives authored. The dihedrals it packs are rotation-
 *   invariant, so the Y-rotation applied here needs no correction.
 *
 *   // INVARIANT: cached model meshes are never mutated. Placement is applied
 *   while copying vertices into the scene buffers.
 *   // INVARIANT: `feat[t]` belongs to scene triangle t. A mesh without `feat`
 *   //   contributes zeros ("no edge data"); a dropped degenerate triangle takes
 *   //   its entry with it and un-pairs the kept triangle that expected it.
 */
import type { Connection, FlowMedium, LegNote, Shape } from '../types';
import { S } from '../engine/state';
import { computeBalance, streamCaption, streamColourOf } from '../engine/balance';
import { hexTriple } from '../engine/formulation';
import { v3, type Vec2, type Vec3 } from './math3';
import { MATERIALS, hexToRgb, type MatKey } from './materials';
import { F_PAIR, type Mesh } from './mesh';
import { getModelFor, depthForShape } from './models';
import { buildPipe, pipeLabelAnchor, portWorld } from './pipes';
import { anchorsOf, buildPipe3, legSpans, noteInSpan, radiusOf } from './pipes3';
import { ensureLevels } from './levels';
import { DEFAULT_OFF, endsOfDim, textOf } from './dims3';
import { placeOf, yawRadians } from './placement';
import { buildStructure, isStructureVisible } from './structure';
import { stepFor } from './flow';
import {
  directionOfConnection,
  directionOfPipe3,
  isServiceLine,
  mediumOfConnection,
  mediumOfPipe3,
  shapeIndex,
} from '../engine/streams';

/** Stable material ordering — the index stored per triangle. */
export const MAT_KEYS = Object.keys(MATERIALS) as MatKey[];
const MAT_INDEX: Record<string, number> = {};
MAT_KEYS.forEach((k, i) => (MAT_INDEX[k] = i));

/** One thing in the scene: an equipment item, a pipe run, or the steelwork. */
export interface SceneObject {
  id: string;
  kind: 'shape' | 'conn' | 'pipe3' | 'structure';
  type: string;
  label: string;
  tag: string;
  /** Where the floating label is tethered (top of the object). */
  labelAt: Vec3;
  min: Vec3;
  max: Vec3;
  /** Convex ground outline in world XZ, for the shadow pass. Empty = no shadow. */
  shadow: Vec2[];
  /** Bottom and top of the shadow caster, so elevated items cast correctly. */
  shadowY0: number;
  shadowY1: number;
  /** Colour pulled from the 2D document, mixed into the material. */
  tint: [number, number, number] | null;
  tintAmt: number;
  /** Level id, for the HUD read-out. */
  level?: string;
  /**
   * What this run carries when the flow is run. Only pipework has one — an
   * absent value means "this is not a pipe", not "nothing flows in it", which
   * is what `'none'` says.
   */
  flow?: FlowMedium;
  /**
   * True when the contents travel against the way the run was drawn — a vacuum
   * line, most often. The distances along the run are unchanged; this only
   * turns the slugs round.
   */
  flowRev?: boolean;
  /**
   * The colour this run's contents move in, overriding the medium's default.
   *
   * What lets you watch the chalk and the resin travel down their own pipes and
   * come out as one compound. Absent on a run carrying a blend, which keeps the
   * ordinary material amber — painting a mixture as whichever ingredient
   * happens to be heaviest would be a lie you could watch.
   */
  flowCol?: [number, number, number];
}

/**
 * One straight leg of pipework, for the dimension pass.
 * Held as world endpoints rather than a length, so the renderer can place the
 * dimension line off to the side of the leg in screen space.
 */
export interface DimLeg {
  a: Vec3;
  b: Vec3;
  /** The run this leg belongs to, so a selected pipe can be dimensioned alone. */
  id: string;
  /** This leg's index among its run's own vertices. */
  seg: number;
  /** The stretch of the run this leg occupies, 0..1 — how a note finds it. */
  t0: number;
  t1: number;
  /** Your own caption, when one has been typed on this leg. */
  text?: string;
  /** True for pipework drawn in 3D, whose legs can be driven to a length. */
  own: boolean;
}

/** A hand-placed dimension, resolved and ready to draw. */
export interface UserDim {
  id: string;
  a: Vec3;
  b: Vec3;
  /** Already-formatted text: the user's own, or the measured length. */
  text: string;
  /** Offset of the dimension line from the measured line, in screen pixels. */
  off: number;
  flip: boolean;
}

/** A floor, as the renderer needs it for the datum markers down the side. */
export interface SceneLevel {
  id: string;
  name: string;
  elevation: number;
}

export interface Scene {
  /** 9 floats per triangle: three xyz vertex positions. */
  pos: Float32Array;
  /** 9 floats per triangle: three xyz vertex normals. */
  nrm: Float32Array;
  /** Material index per triangle. */
  mat: Uint8Array;
  /** Owning object index per triangle. */
  obj: Uint16Array;
  /**
   * How far along its run each pipe triangle sits, in world units; -1 for
   * everything that is not pipe wall.
   *
   * This is the whole basis of the flow animation. Holding it per triangle,
   * built once with the scene, is what lets a slug be painted by the shader
   * that was already colouring that triangle — no extra geometry, no second
   * pass, and occlusion that is simply correct because the moving thing IS the
   * pipe rather than something drawn over the top of it.
   */
  flowS: Float32Array;
  /**
   * Packed edge data per triangle (kit spec §4.1): feature / smooth edge bits,
   * F_PAIR, F_DECAL and three int8 dihedrals. Length === count. Optional so a
   * hand-made scene without it reads as "no edge data".
   */
  feat?: Uint32Array;
  count: number;
  objects: SceneObject[];
  /** Floors, bottom to top — drawn as an elevation datum down the near edge. */
  levels: SceneLevel[];
  /** Straight pipe legs, for the automatic dimension pass. */
  dims: DimLeg[];
  /** Dimensions placed by hand. */
  userDims: UserDim[];
  min: Vec3;
  max: Vec3;
}

/** The empty scene, used before the first build and for an empty document. */
export function emptyScene(): Scene {
  return {
    pos: new Float32Array(0),
    nrm: new Float32Array(0),
    mat: new Uint8Array(0),
    obj: new Uint16Array(0),
    flowS: new Float32Array(0),
    feat: new Uint32Array(0),
    count: 0,
    objects: [],
    levels: [],
    dims: [],
    userDims: [],
    min: v3(-200, 0, -200),
    max: v3(200, 100, 200),
  };
}

/** Growable triangle accumulator — resized by doubling, trimmed once at the end. */
class TriBuffer {
  pos = new Float32Array(9 * 4096);
  nrm = new Float32Array(9 * 4096);
  mat = new Uint8Array(4096);
  obj = new Uint16Array(4096);
  /** Distance along its run for pipe wall; -1 for everything else. */
  flowS = new Float32Array(4096).fill(-1);
  /** Packed edge data per triangle (0 = none), copied from `Mesh.feat`. */
  feat = new Uint32Array(4096);
  n = 0;

  private grow(need: number): void {
    if (this.n + need <= this.mat.length) return;
    let cap = this.mat.length;
    while (cap < this.n + need) cap *= 2;
    const pos = new Float32Array(cap * 9);
    pos.set(this.pos);
    this.pos = pos;
    const nrm = new Float32Array(cap * 9);
    nrm.set(this.nrm);
    this.nrm = nrm;
    const mat = new Uint8Array(cap);
    mat.set(this.mat);
    this.mat = mat;
    const obj = new Uint16Array(cap);
    obj.set(this.obj);
    this.obj = obj;
    // -1 is "not pipe wall", so the fresh tail has to start there rather than
    // at the zero a new typed array would give it — which would be a valid
    // distance, and would set the whole plant flowing at its first slug.
    const flowS = new Float32Array(cap).fill(-1);
    flowS.set(this.flowS);
    this.flowS = flowS;
    const feat = new Uint32Array(cap);
    feat.set(this.feat);
    this.feat = feat;
  }

  /**
   * Append a mesh, applying a Y-rotation and a world translation on the way in.
   * Degenerate triangles are dropped here rather than wasting a sort slot and a
   * canvas fill on every frame for the rest of the session.
   *
   * @param arc  Optional per-VERTEX distance along a run, from `tube`. Each
   *   triangle takes the mean of its three, which is what the flow animation
   *   reads. Passed through here rather than worked out afterwards because
   *   dropping degenerate triangles is what makes the two lists disagree.
   */
  add(
    m: Mesh,
    matIdx: number,
    objIdx: number,
    cos: number,
    sin: number,
    tx: number,
    ty: number,
    tz: number,
    arc?: number[],
  ): void {
    const tris = m.idx.length / 3;
    this.grow(tris);
    const mf = m.feat;
    // Source index of the last triangle of THIS mesh that was kept, so a
    // dropped partner can un-pair it (F_PAIR says "t and t+1 are one quad").
    let lastKept = -2;
    for (let t = 0; t < tris; t++) {
      const base = this.n * 9;
      let degenerate = true;
      let px = 0;
      let py = 0;
      let pz = 0;
      for (let k = 0; k < 3; k++) {
        const vi = m.idx[t * 3 + k] * 3;
        const x = m.pos[vi];
        const y = m.pos[vi + 1];
        const z = m.pos[vi + 2];
        const wx = x * cos + z * sin + tx;
        const wy = y + ty;
        const wz = -x * sin + z * cos + tz;
        this.pos[base + k * 3] = wx;
        this.pos[base + k * 3 + 1] = wy;
        this.pos[base + k * 3 + 2] = wz;
        const nx = m.nrm[vi];
        const ny = m.nrm[vi + 1];
        const nz = m.nrm[vi + 2];
        this.nrm[base + k * 3] = nx * cos + nz * sin;
        this.nrm[base + k * 3 + 1] = ny;
        this.nrm[base + k * 3 + 2] = -nx * sin + nz * cos;
        if (k === 0) {
          px = wx;
          py = wy;
          pz = wz;
        } else if (degenerate && (Math.abs(wx - px) > 1e-4 || Math.abs(wy - py) > 1e-4 || Math.abs(wz - pz) > 1e-4)) {
          degenerate = false;
        }
      }
      if (degenerate) {
        if (lastKept === t - 1 && this.n > 0) this.feat[this.n - 1] &= ~F_PAIR;
        continue;
      }
      let f = mf && t < mf.length ? mf[t] >>> 0 : 0;
      // A pair never spans two meshes: the last triangle has no partner here.
      if (t === tris - 1) f &= ~F_PAIR;
      this.feat[this.n] = f >>> 0;
      lastKept = t;
      this.mat[this.n] = matIdx;
      this.obj[this.n] = objIdx;
      this.flowS[this.n] = arc
        ? (arc[m.idx[t * 3]] + arc[m.idx[t * 3 + 1]] + arc[m.idx[t * 3 + 2]]) / 3
        : -1;
      this.n++;
    }
  }

  /** Bounds of the triangles appended since `from`. */
  boundsSince(from: number): { min: Vec3; max: Vec3; ok: boolean } {
    let ax = Infinity;
    let ay = Infinity;
    let az = Infinity;
    let bx = -Infinity;
    let by = -Infinity;
    let bz = -Infinity;
    for (let t = from; t < this.n; t++)
      for (let k = 0; k < 3; k++) {
        const i = t * 9 + k * 3;
        const px = this.pos[i];
        const py = this.pos[i + 1];
        const pz = this.pos[i + 2];
        if (px < ax) ax = px;
        if (px > bx) bx = px;
        if (py < ay) ay = py;
        if (py > by) by = py;
        if (pz < az) az = pz;
        if (pz > bz) bz = pz;
      }
    return { min: v3(ax, ay, az), max: v3(bx, by, bz), ok: Number.isFinite(ax) };
  }
}

/**
 * Colours the 2D drawing uses as INK rather than as a description of the thing
 * drawn: plain white fills, and the default black and grey a connector is
 * created with. Tinting a pipe with those turns bare steel into a black or
 * white pipe, which is not what leaving a connector at its default meant.
 */
const NEUTRAL_INK = new Set(['#ffffff', '#fff', '#000000', '#000', '#1a1a1a', '#aaaaaa', '#aaa']);

/** Mix a document colour into the model materials, unless it is only ink. */
function tintOf(hex: string | undefined, amount: number): { tint: [number, number, number] | null; amt: number } {
  if (!hex) return { tint: null, amt: 0 };
  const h = hex.trim().toLowerCase();
  if (!h.startsWith('#') || NEUTRAL_INK.has(h)) return { tint: null, amt: 0 };
  return { tint: hexToRgb(h), amt: amount };
}

/**
 * A pipe's colour. An explicit 3D colour is paint and is applied hard; a
 * colour inherited from the 2D connector is a hint and is applied gently, so a
 * drawing that was colour-coded for legibility does not come out looking
 * like a toy.
 */
function pipeTint(color3: string | undefined, color2: string | undefined) {
  if (color3) return tintOf(color3, 0.85);
  return tintOf(color2, 0.62);
}

const FLAT = new Set(['zonebox', 'textlabel', 'processarrow']);

/** True when a shape lies on the floor rather than standing up on it. */
function isFlat(s: Shape): boolean {
  // A zone box stood up as a wall or a room is a building surface, not a floor
  // marking: it casts a real shadow and carries its label at the top.
  if (s.type === 'zonebox') return (s.mode3 ?? 'floor') === 'floor';
  return FLAT.has(s.type);
}

/**
 * Build the whole scene from the current document.
 *
 * @param flowing  True while the flow is running, which sweeps the pipe wall
 *   into finer sections so a slug has something to be drawn across. How fine is
 *   decided per run by what it carries (`stepFor`). Off by default: it is
 *   several times the triangles on every pipe in the plant, and it buys nothing
 *   at all when nothing is moving.
 */
export function buildScene(scaleMm = 25, flowing = false): Scene {
  // Derive floors the first time a drawing is opened in 3D.
  ensureLevels();

  const buf = new TriBuffer();
  const objects: SceneObject[] = [];
  let x0 = Infinity;
  const y0 = 0;
  let z0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let z1 = -Infinity;

  const track = (min: Vec3, max: Vec3) => {
    if (min.x < x0) x0 = min.x;
    if (max.x > x1) x1 = max.x;
    if (max.y > y1) y1 = max.y;
    if (min.z < z0) z0 = min.z;
    if (max.z > z1) z1 = max.z;
  };

  // ── Steelwork ──
  // First, so the painter's sort resolves ties in favour of what stands on it.
  const structure = buildStructure();
  if (structure.length) {
    const objIdx = objects.length;
    const start = buf.n;
    for (const part of structure) buf.add(part.mesh, MAT_INDEX[part.mat], objIdx, 1, 0, 0, 0, 0);
    const b = buf.boundsSince(start);
    if (b.ok) track(b.min, b.max);
    objects.push({
      id: '__structure__',
      kind: 'structure',
      type: 'structure',
      label: '',
      tag: '',
      labelAt: v3(0, 0, 0),
      min: b.min,
      max: b.max,
      shadow: [],
      shadowY0: 0,
      shadowY1: 0,
      tint: null,
      tintAmt: 0,
    });
  }

  // ── Equipment ──
  // Zone boxes first so their floor markings never z-fight the machines that
  // stand inside them.
  const shapes = S.shapes
    // Hidden in 3D: the 2D drawing keeps the symbol, this view simply does not
    // draw it. Nothing else about the document changes.
    .filter((s) => !s.hide3)
    .sort((a, b) => {
    const az = a.type === 'zonebox' ? -1 : (a.zIndex || 0);
    const bz = b.type === 'zonebox' ? -1 : (b.zIndex || 0);
    return az - bz;
  });

  for (const s of shapes) {
    const model = getModelFor(s);
    const objIdx = objects.length;
    const rot = yawRadians(s);
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const place = placeOf(s);
    const start = buf.n;
    const { tint, amt } = tintOf(s.fillColor, 0.5);

    for (const part of model.parts)
      buf.add(part.mesh, MAT_INDEX[part.mat], objIdx, cos, sin, place.x, place.y, place.z);

    const b = buf.boundsSince(start);
    const min = b.ok ? b.min : v3(place.x - s.w / 2, place.y, place.z - depthForShape(s) / 2);
    const max = b.ok ? b.max : v3(place.x + s.w / 2, place.y + model.height, place.z + depthForShape(s) / 2);
    track(min, max);

    const shadow: Vec2[] = model.footprint.map((q) => ({
      x: q.x * cos + q.y * sin + place.x,
      y: -q.x * sin + q.y * cos + place.z,
    }));

    objects.push({
      id: s.id,
      kind: 'shape',
      type: s.type,
      label: s.label || '',
      tag: s.tag || '',
      labelAt: v3(place.x, max.y, place.z),
      min,
      max,
      shadow,
      shadowY0: isFlat(s) ? 0 : place.y,
      shadowY1: max.y,
      tint,
      tintAmt: amt,
      level: s.level,
    });
  }

  // ── Pipework ──
  const dims: DimLeg[] = [];
  /** Record the straight legs of a run, skipping any too short to dimension. */
  const addDims = (id: string, line: Vec3[], own: boolean, host?: { legNotes3?: LegNote[] }) => {
    const spans = legSpans(line);
    for (let i = 1; i < line.length; i++) {
      const a = line[i - 1];
      const b = line[i];
      if (Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) < 12) continue;
      const note = host ? noteInSpan(host.legNotes3, spans[i - 1]?.t0 ?? 0, spans[i - 1]?.t1 ?? 1) : undefined;
      // A leg the engineer has switched off is not dimensioned at all. Real
      // drawings dimension what matters, not every straight on the sheet.
      if (note?.off) continue;
      const sp = spans[i - 1];
      dims.push({ a, b, id, seg: i - 1, t0: sp?.t0 ?? 0, t1: sp?.t1 ?? 1, text: note?.text, own });
    }
  };

  const byId = shapeIndex(S.shapes);
  const hidden = (id: string | undefined): boolean => !!id && !!byId.get(id)?.hide3;
  // Throughputs, worked out once for the whole drawing. A run's figure rides on
  // its label rather than getting a pass of its own: the label pass already
  // knows how to keep chips off each other and off the machinery, and a
  // throughput has exactly the same problem.
  const bal = computeBalance();
  /** What one run's contents move in, when the balance knows what they are. */
  const flowColour = (id: string): [number, number, number] | undefined => {
    const hex = streamColourOf(bal, id);
    return hex ? hexTriple(hex) : undefined;
  };
  const runLabel = (id: string, own: string): string => {
    const cap = streamCaption(bal, id);
    if (!cap) return own;
    return own ? `${own}  ·  ${cap}` : cap;
  };
  const pipeMat = MAT_INDEX.pipe;
  const fitMat = MAT_INDEX.machine;
  const lagMat = MAT_INDEX.lagging;
  const galvMat = MAT_INDEX.galv;
  /**
   * The pipe builders' optional style (kit spec §8.1): supports only while the
   * steelwork is shown, compression couplings only on pneumatic-conveying runs
   * (material or air, not a service line). Passed through a widened signature
   * so this compiles against both the current builders and the ones that read
   * it; a builder that does not take the argument simply ignores it.
   */
  type PipeStyle = { supports?: boolean; couplings?: boolean };
  const buildPipeStyled = buildPipe as (
    cn: Connection,
    a: ReturnType<typeof portWorld>,
    b: ReturnType<typeof portWorld>,
    flowStep?: number,
    style?: PipeStyle,
  ) => ReturnType<typeof buildPipe>;
  const buildPipe3Styled = buildPipe3 as (
    q: (typeof S.pipes3)[number],
    flowStep?: number,
    style?: PipeStyle,
  ) => ReturnType<typeof buildPipe3>;
  const supportsOn = isStructureVisible();

  for (const cn of S.connections as Connection[]) {
    // Hidden in 3D: the 2D drawing keeps its connector, this view does not draw
    // it. That is what "delete the pipe" means here.
    if (cn.hide3) continue;
    // A pipe exists to serve its equipment; hide the equipment and the pipe
    // goes with it rather than dangling in mid-air.
    if (hidden(cn.fromId) || hidden(cn.toId)) continue;
    const f = byId.get(cn.fromId);
    const t = byId.get(cn.toId);
    if (!f || !t) continue;
    const a = portWorld(f, cn.fromPort || 'E');
    const b2 = portWorld(t, cn.toPort || 'W');
    const flow = mediumOfConnection(cn, byId);
    let geom;
    try {
      // A run carrying nothing is never animated, so it is never worth the
      // extra sections either — `stepFor` returns 0 for it.
      geom = buildPipeStyled(cn, a, b2, flowing ? stepFor(flow) : 0, {
        supports: supportsOn,
        couplings: flow !== 'none' && !isServiceLine(cn),
      });
    } catch {
      continue; // A single bad run must not sink the whole view.
    }
    const objIdx = objects.length;
    const start = buf.n;
    // The wall carries the distances; the flanges and the arrowhead do not, so
    // a slug stops at the flange face the way anything in a pipe does.
    buf.add(geom.body, isServiceLine(cn) ? lagMat : pipeMat, objIdx, 1, 0, 0, 0, 0, geom.arc);
    buf.add(geom.fittings, fitMat, objIdx, 1, 0, 0, 0, 0);
    // Pipe supports (kit spec §8.1), when the pipe builder provides them: galv,
    // under the SAME object so selecting the run selects its supports, and
    // with no flow distance — a slug never runs down a support post.
    const sup = (geom as { supports?: Mesh }).supports;
    if (sup) buf.add(sup, galvMat, objIdx, 1, 0, 0, 0, 0);

    const b = buf.boundsSince(start);
    if (b.ok) track(b.min, b.max);
    addDims(cn.id, geom.route.path, false, cn);

    const { tint, amt } = pipeTint(cn.color3, cn.color);
    objects.push({
      id: cn.id,
      kind: 'conn',
      type: 'pipe',
      label: runLabel(cn.id, cn.label || ''),
      tag: '',
      labelAt: pipeLabelAnchor(geom.route.path),
      min: b.min,
      max: b.max,
      shadow: [],
      shadowY0: 0,
      shadowY1: 0,
      tint,
      tintAmt: amt,
      flow,
      flowRev: directionOfConnection(cn) === 'reverse',
      flowCol: flowColour(cn.id),
    });
  }

  // ── Pipework drawn in 3D ──
  for (const q of S.pipes3) {
    if (hidden(q.from.shapeId) || hidden(q.to.shapeId)) continue;
    const flow = mediumOfPipe3(q, byId);
    let geom;
    try {
      geom = buildPipe3Styled(q, flowing ? stepFor(flow) : 0, {
        supports: supportsOn,
        couplings: flow !== 'none' && !q.service,
      });
    } catch {
      continue;
    }
    if (!geom) continue;
    const objIdx = objects.length;
    const start = buf.n;
    buf.add(geom.body, q.service ? lagMat : pipeMat, objIdx, 1, 0, 0, 0, 0, geom.arc);
    buf.add(geom.fittings, fitMat, objIdx, 1, 0, 0, 0, 0);
    const sup3 = (geom as { supports?: Mesh }).supports;
    if (sup3) buf.add(sup3, galvMat, objIdx, 1, 0, 0, 0, 0);
    const b = buf.boundsSince(start);
    if (b.ok) track(b.min, b.max);
    addDims(q.id, anchorsOf(q), true, q);
    const { tint, amt } = pipeTint(q.color, undefined);
    // Anchor the label on the middle vertex of the run rather than an end,
    // where every other pipe's label already is.
    const mid = geom.line[Math.floor(geom.line.length / 2)];
    objects.push({
      id: q.id,
      kind: 'pipe3',
      type: 'pipe3',
      label: runLabel(q.id, q.label || ''),
      tag: '',
      labelAt: v3(mid.x, mid.y + radiusOf(q) * 2, mid.z),
      min: b.min,
      max: b.max,
      shadow: [],
      shadowY0: 0,
      shadowY1: 0,
      tint,
      tintAmt: amt,
      flow,
      flowRev: directionOfPipe3(q) === 'reverse',
      flowCol: flowColour(q.id),
    });
  }

  // ── Hand-placed dimensions ──
  // Resolved here rather than in the renderer so an anchor on a piece of
  // equipment tracks that equipment automatically.
  const userDims: UserDim[] = [];
  for (const d of S.dims3) {
    const ends = endsOfDim(d);
    if (!ends) continue;
    if (hidden(d.from.shapeId) || hidden(d.to.shapeId)) continue;
    userDims.push({
      id: d.id,
      a: ends.a,
      b: ends.b,
      text: textOf(d, scaleMm),
      off: d.off ?? DEFAULT_OFF,
      flip: !!d.flip,
    });
  }

  if (!Number.isFinite(x0)) {
    x0 = -200;
    z0 = -200;
    x1 = 200;
    z1 = 200;
    y1 = 100;
  }

  return {
    pos: buf.pos.subarray(0, buf.n * 9),
    nrm: buf.nrm.subarray(0, buf.n * 9),
    mat: buf.mat.subarray(0, buf.n),
    obj: buf.obj.subarray(0, buf.n),
    flowS: buf.flowS.subarray(0, buf.n),
    feat: buf.feat.subarray(0, buf.n),
    count: buf.n,
    objects,
    levels: S.levels.map((l) => ({ id: l.id, name: l.name, elevation: l.elevation })),
    dims,
    userDims,
    min: v3(x0, y0, z0),
    max: v3(x1, y1, z1),
  };
}
